import { useCallback, useEffect, useState } from "react";
import { RTFOPumpdownChart } from "./RTFOPumpdownChart";
import { RTFOPumpdownFalloffChart } from "./RTFOPumpdownFalloffChart";
import { Accordion, AccordionDetails, AccordionSummary, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Cancel, ExpandMore, Save, VerticalAlignBottom } from "@mui/icons-material";
import { useSocket } from "../../context/useSocket";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { setStageData } from "../../reducers/rtfoSlice";
import { RTFOPumpdownDataTable } from "./RTFOPumpdownDataTable";
import { RTFOPumpdownDeltaChart } from "./RTFOPumpdownDeltaChart";

export const RTFOPumpdown = ({ currentRTFO, stageData, pumpdownData, isLoadingPumpdownData, gridView, darkMode }) => {
  
  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [pumpdownFalloffTimestamps, setPumpdownFalloffTimestamps] = useState(() => {
    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps) {
      return stageData?.pumpdownParams?.pumpdownFalloffTimestamps;
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1);
      const falloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const falloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > falloffStartTimestamp && d.rate > 0.1)?.timestamp;
      return { startTimestamp: falloffStartTimestamp, endTimestamp: falloffEndTimestamp };
    };
  });

  const [postPerfFalloffTimestamps, setPostPerfFalloffTimestamps] = useState(() => {
    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps) {
      return stageData?.pumpdownParams?.postPerfFalloffTimestamps;
    } else {
      const falloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const falloffStartTimestamp = pumpdownData?.[falloffStartIndex]?.timestamp;
      const falloffStartDepth = pumpdownData?.[falloffStartIndex]?.depth;
      const falloffEndTimestamp = pumpdownData?.findLast((d, i) => i > falloffStartIndex && (Math.abs(falloffStartDepth - d.depth)) < 100)?.timestamp;
      return { startTimestamp: falloffStartTimestamp, endTimestamp: falloffEndTimestamp };
    };
  });

  const [editMode, setEditMode] = useState(false);
  const [pumpdownISIP, setPumpdownISIP] = useState(stageData?.pumpdownParams?.pumpdownISIP || 0);
  const [postPerfISIP, setPostPerfISIP] = useState(stageData?.pumpdownParams?.postPerfISIP || 0);

  // when pumpdown data changes, update the timestamps
  useEffect(() => {
    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps) {
      setPumpdownFalloffTimestamps(stageData?.pumpdownParams?.pumpdownFalloffTimestamps);
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1);
      const pumpdownFalloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const pumpdownFalloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > pumpdownFalloffStartTimestamp && d.rate > 0.1)?.timestamp;
      setPumpdownFalloffTimestamps({ startTimestamp: pumpdownFalloffStartTimestamp, endTimestamp: pumpdownFalloffEndTimestamp });
    };

    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps) {
      setPostPerfFalloffTimestamps(stageData?.pumpdownParams?.postPerfFalloffTimestamps);
    } else {
      const postPerfFalloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const postPerfFalloffStartTimestamp = pumpdownData?.[postPerfFalloffStartIndex]?.timestamp;
      const postPerfFalloffStartDepth = pumpdownData?.[postPerfFalloffStartIndex]?.depth;
      const postPerfFalloffEndTimestamp = pumpdownData?.findLast((d, i) => i > postPerfFalloffStartIndex && (Math.abs(postPerfFalloffStartDepth - d.depth)) < 100)?.timestamp;
      setPostPerfFalloffTimestamps({ startTimestamp: postPerfFalloffStartTimestamp, endTimestamp: postPerfFalloffEndTimestamp });
    };
  }, [stageData, pumpdownData]);


  const updatePumpdownISIP = useCallback((isip) => {
    setPumpdownISIP(isip);
  }, []);
  const updatePostPerfISIP = useCallback((isip) => {
    setPostPerfISIP(isip);
  }, []);

  return (
    <div style={{display: 'flex', flexDirection: 'column'}}>
      <div style={{position: 'relative', width: '100%', display: 'flex', flexDirection: gridView ? 'row' : 'column'}}>
        <div style={{width: gridView ? '50%' : '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'absolute', top: 10, left: 10, paddingRight: 20, zIndex: 421}}>
          <div style={{ display: 'flex', flexDirection: 'row'}}>
            <ToggleButtonGroup
              value={editMode}
              exclusive
              onChange={(e, newType) => setEditMode(newType)}
              size="small"
              style={{height: 24, zIndex: 420}}
            >
              <ToggleButton value="pumpdownStart" size="small" title="Edit Pumpdown Start" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value="pumpdownEnd" size="small" title="Edit Pumpdown End" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" style={{ margin: 5 }} />

            <ToggleButtonGroup
              value={editMode}
              exclusive
              onChange={(e, newType) => setEditMode(newType)}
              size="small"
              style={{height: 24, zIndex: 420}}
            >
              <ToggleButton value="postPerfStart" size="small" title="Edit Post Perf Start" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value="postPerfEnd" size="small" title="Edit Post Perf End" color={(darkMode ? "" : "primary")}>
                <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <ToggleButtonGroup
            value={''}
            size="small"
            style={{height: 24, display: !editMode && 'none'}}
            onChange={() => {}}
          >
            <ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges} >
              <Cancel style={{fontSize: '16px'}} />
            </ToggleButton>
            <ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={handleSaveChanges}>
              <Save style={{fontSize: '16px'}} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 580 : 900, paddingTop: 40, outline: editMode && '5px solid red', zIndex: 420, position: 'relative'}}>
          <div 
            style={{
              zIndex: 69, 
              position: 'absolute', 
              left: '50%', 
              top: 0, 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
            }}
          >
            {editMode && <Typography variant="h6" color="red">EDIT MODE</Typography>}
          </div>
          <div 
            style={{
              zIndex: 69, 
              position: 'absolute', 
              left: '50%', 
              bottom: 0, 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
            }}
          >
            {editMode === 'pumpdownStart' ? <Typography variant="body2" color="red">Click to move the pumpdown start time</Typography> : 
            editMode === 'pumpdownEnd' ? <Typography variant="body2" color="red">Click to move the pumpdown end time</Typography> : 
            editMode === 'postPerfStart' ? <Typography variant="body2" color="red">Click to move the post perf start time</Typography> : 
            editMode === 'postPerfEnd' ? <Typography variant="body2" color="red">Click to move the post perf end time</Typography> : ''}
          </div>
          
          <RTFOPumpdownChart
            gridView={gridView} 
            showSpikeLines={true} 
            pumpdownData={pumpdownData}
            pumpdownFalloffTimestamps={pumpdownFalloffTimestamps}
            setPumpdownFalloffTimestamps={setPumpdownFalloffTimestamps}
            postPerfFalloffTimestamps={postPerfFalloffTimestamps}
            setPostPerfFalloffTimestamps={setPostPerfFalloffTimestamps}
            isLoadingPumpdownData={isLoadingPumpdownData}
            editMode={editMode}
          />
        </div>
        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 580 : 900, paddingTop: 40}}>
          <RTFOPumpdownFalloffChart
            gridView={gridView} 
            showSpikeLines={true} 
            pumpdownData={pumpdownData}
            pumpdownFalloffTimestamps={pumpdownFalloffTimestamps}
            postPerfFalloffTimestamps={postPerfFalloffTimestamps}
            pumpdownISIP={pumpdownISIP}
            updatePumpdownISIP={updatePumpdownISIP}
            postPerfISIP={postPerfISIP}
            updatePostPerfISIP={updatePostPerfISIP}
            isLoadingPumpdownData={isLoadingPumpdownData}
          />
        </div>
      </div>

      <Accordion defaultExpanded disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Pumpdown Results
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <div style={{height: 540, width: '50%'}}>
              <RTFOPumpdownDeltaChart currentRTFO={currentRTFO} gridView={true} />
            </div>
            <div style={{ height: 700, width: '50%' }}>
              <RTFOPumpdownDataTable currentRTFO={currentRTFO} pumpdownParams={stageData?.pumpdownParams} darkMode={darkMode} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

    </div>
  );

  function handleCancelChanges() {
    setEditMode(false);

    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps) {
      setPumpdownFalloffTimestamps(stageData?.pumpdownParams?.pumpdownFalloffTimestamps);
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1);
      const pumpdownFalloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const pumpdownFalloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > pumpdownFalloffStartTimestamp && d.rate > 0.1)?.timestamp;
      setPumpdownFalloffTimestamps({ startTimestamp: pumpdownFalloffStartTimestamp, endTimestamp: pumpdownFalloffEndTimestamp });
    };

    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps) {
      setPostPerfFalloffTimestamps(stageData?.pumpdownParams?.postPerfFalloffTimestamps);
    } else {
      const postPerfFalloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const postPerfFalloffStartTimestamp = pumpdownData?.[postPerfFalloffStartIndex]?.timestamp;
      const postPerfFalloffStartDepth = pumpdownData?.[postPerfFalloffStartIndex]?.depth;
      const postPerfFalloffEndTimestamp = pumpdownData?.findLast((d, i) => i > postPerfFalloffStartIndex && (Math.abs(postPerfFalloffStartDepth - d.depth)) < 100)?.timestamp;
      setPostPerfFalloffTimestamps({ startTimestamp: postPerfFalloffStartTimestamp, endTimestamp: postPerfFalloffEndTimestamp });
    };
  };

  function handleSaveChanges() {
    setEditMode(false);

    const pumpdownParams = {
      pumpdownFalloffTimestamps,
      postPerfFalloffTimestamps,
      pumpdownISIP,
      postPerfISIP
    };

    dispatch(setStageData({stageData: {...stageData, pumpdownParams}, seismicData: null, initialLoad: false, highResolutionData: true, liveMonitoring: false, incrementalData: false}));

    socket?.send(JSON.stringify({
      messageType: 'savePumpdownParams',
      stageName: stageData?.name,
      pumpdownParams
    }));
    enqueueSnackbar(`Stage ${stageData?.name} saved successfully`, { variant: 'success' });
  };
};